<script setup lang="ts">
import type { BaseDropdownOption, BaseDropdownOptionGroup } from '#imports';
import { ComboboxInput } from 'reka-ui';
import type { ComponentPublicInstance } from 'vue';

const activeElement = useActiveElement();
const itemsActive = ref<boolean>(false);
const itemsSelect = ref<string[]>([]);
const input = ref<ComponentPublicInstance<typeof ComboboxInput> | null>(null);
const inputIsFocused = ref<boolean>(false);

const emit = defineEmits<{
  multipleSelected: [string[]];
  singleSelected: [string];
}>();

defineProps<{
  dropdownId: string;
  placeholder: string;
  allowMultipleOptions: boolean;
  showMultipleValuesInInput?: boolean;
  dropdownLabel?: string;
  items?: BaseDropdownOption[];
  groups?: BaseDropdownOptionGroup[];
  cssClassOverride?: {
    dropdownLabel?: string;
    dropdownTrigger?: string;
    dropdownContent?: string;
    dropdownItem?: string;
    dropdownGroup?: string;
    dropdownGroupLabel?: string;
  };
}>();

watch(activeElement, () => {
  if (activeElement.value === input.value?.$el) {
    inputIsFocused.value = true;
  }
  if (activeElement.value !== input.value?.$el) {
    inputIsFocused.value = false;
  }
});

const selectedValues = ref<string[]>([]);

function addSelectedValues(selectedOption: string[] | string) {
  if (Array.isArray(selectedOption)) {
    selectedValues.value = selectedOption;
    emit('multipleSelected', selectedOption);
  }
  else {
    emit('singleSelected', selectedOption);
  }
}

function preventToggleOnOptionDelete() {
  itemsActive.value = true;
}
</script>

<template>
  <article>
    <Label
      v-if="dropdownLabel"
      :for="dropdownId"
      class="mb-2 text-base font-bold"
      :class="cssClassOverride?.dropdownLabel"
      as="p"
    >
      {{ dropdownLabel }}
    </Label>
    <ComboboxRoot
      v-model="itemsSelect"
      v-model:open="itemsActive"
      :multiple="allowMultipleOptions"
      class="mb-2"
      @update:model-value="addSelectedValues"
    >
      <ComboboxAnchor>
        <ComboboxTrigger
          :id="dropdownId"
          :class="
            cssClassOverride?.dropdownTrigger || [
              'relative flex min-w-72 items-center justify-between border border-black px-4 py-5 transition-colors',
              inputIsFocused ? 'outline outline-1' : 'outline-0',
            ]
          "
        >
          <ComboboxInput
            v-if="showMultipleValuesInInput === false"
            ref="input"
            :placeholder="placeholder"
            readonly
            class="cursor-pointer bg-transparent outline-none outline-0 selection:bg-transparent selection:text-main"
          />

          <TagsInputRoot
            v-if="allowMultipleOptions && showMultipleValuesInInput"
            v-model:model-value="selectedValues"
            class="flex max-w-72 flex-wrap gap-2 leading-3"
            @update:model-value="preventToggleOnOptionDelete"
          >
            <TransitionGroup name="scale-in">
              <TagsInputItem
                v-for="item in selectedValues"
                :key="`${item}-tag`"
                :value="item"
                class="group cursor-pointer bg-gray-30 leading-3"
              >
                <TagsInputItemDelete class="flex items-center gap-2 p-2">
                  <TagsInputItemText />
                  <BaseIcon
                    name="close"
                    class="p-2 text-xs leading-3 group-hover:bg-tertiary group-hover:text-gray-30"
                  />
                </TagsInputItemDelete>
              </TagsInputItem>
            </TransitionGroup>

            <TagsInputInput
              v-if="!selectedValues.length"
              :placeholder="placeholder"
              tabindex="-1"
              readonly
              class="cursor-pointer bg-transparent outline-none outline-0 selection:bg-transparent selection:text-main"
            />
            <VisuallyHidden>
              <ComboboxInput ref="input" />
            </VisuallyHidden>
          </TagsInputRoot>

          <BaseIcon
            name="chevron-down"
            prefix="arrows"
            :class="['ml-2 text-icon transition', { 'rotate-180': itemsActive }]"
          />
        </ComboboxTrigger>
      </ComboboxAnchor>

      <ComboboxPortal>
        <Transition name="dropdown-fade">
          <ComboboxContent
            class="select-none"
            :class="
              cssClassOverride?.dropdownContent || [
                'z-40 max-h-64 min-w-72 bg-white shadow-md',
                { 'border-t': itemsActive && inputIsFocused },
              ]
            "
            position="popper"
            :align="'start'"
            :body-lock="false"
          >
            <ComboboxViewport v-if="items">
              <ComboboxItem
                v-for="(item, index) in items"
                :key="`${item.encodedValue}-${index}`"
                :value="item.displayValue"
                :class="
                  cssClassOverride?.dropdownItem
                    || 'relative px-4 py-2.5 hover:cursor-pointer data-[highlighted]:bg-gray-30'
                "
              >
                <Label
                  as="p"
                  class="flex w-full items-center justify-between text-sm"
                >
                  {{ item.displayValue }}

                  <Transition name="rotate">
                    <ComboboxItemIndicator>
                      <BaseIcon
                        v-if="allowMultipleOptions"
                        name="check"
                      />
                    </ComboboxItemIndicator>
                  </Transition>
                </Label>
              </ComboboxItem>
            </ComboboxViewport>

            <ComboboxViewport v-if="groups">
              <ComboboxGroup
                v-for="(group, index) in groups"
                :key="`${group.displayValue}-${index}`"
              >
                <Label
                  as="p"
                  :class="
                    cssClassOverride?.dropdownGroupLabel || 'border-b px-4 pb-1.5 pt-3 font-bold first-of-type:pt-1.5'
                  "
                >
                  {{ group.displayValue }}

                  <ComboboxItem
                    v-for="(item, i) in group.options"
                    :key="`${item.encodedValue}-${i}`"
                    :value="item.displayValue"
                    :class="
                      cssClassOverride?.dropdownItem
                        || 'relative px-4 py-2.5 hover:cursor-pointer data-[highlighted]:bg-gray-30'
                    "
                  >
                    <Label
                      as="p"
                      class="flex items-center justify-between text-sm"
                    >
                      {{ item.displayValue }}

                      <Transition name="rotate">
                        <ComboboxItemIndicator>
                          <BaseIcon
                            v-if="allowMultipleOptions"
                            name="check"
                          />
                        </ComboboxItemIndicator>
                      </Transition>
                    </Label>
                  </ComboboxItem>
                </Label>
              </ComboboxGroup>
            </ComboboxViewport>
          </ComboboxContent>
        </Transition>
      </ComboboxPortal>
    </ComboboxRoot>

    <TagsInputRoot
      v-if="allowMultipleOptions && !showMultipleValuesInInput"
      v-model:model-value="selectedValues"
      class="flex max-w-72 flex-wrap gap-2 leading-3"
    >
      <TransitionGroup name="scale-in">
        <TagsInputItem
          v-for="item in selectedValues"
          :key="`${item}-tag`"
          :value="item"
          class="group cursor-pointer bg-gray-30 leading-3"
        >
          <TagsInputItemDelete
            class="flex items-center gap-2 p-2"
            tabindex="0"
          >
            <TagsInputItemText />
            <BaseIcon
              name="close"
              class="p-2 text-xs leading-3 group-hover:bg-tertiary group-hover:text-gray-30"
            />
          </TagsInputItemDelete>
        </TagsInputItem>
      </TransitionGroup>
    </TagsInputRoot>
  </article>
</template>
